import { Injectable } from '@angular/core';
import { BaseService } from "@services/BaseService";
import { environment } from '@environments/environment';
import { Observable, throwError, of, BehaviorSubject } from 'rxjs';
import { Router } from '@angular/router';
import { HttpClient } from "@angular/common/http";
import { catchError, map, finalize } from 'rxjs/operators';
import { AuthLoginModel } from "@models/auth/AuthLoginModel";
import { AuthLoginResult } from "@models/auth/AuthLoginResult";

@Injectable({
    providedIn: 'root'
})
export class AuthService extends BaseService
{

    // private fields
    private urlPart: string = 'Auth';
    private authLocalStorageToken = 'AuthUser';

    // public fields
    currentUser$: Observable<AuthLoginResult | undefined>;
    isLoading$: Observable<boolean>;
    currentUserSubject: BehaviorSubject<AuthLoginResult | undefined>;
    isLoadingSubject: BehaviorSubject<boolean>;

    constructor(private http: HttpClient, private router: Router) {
        super(http, router, null);
        this.apiSegmentUrl = this.urlPart;
        
        this.isLoadingSubject = new BehaviorSubject<boolean>(false);
        this.currentUserSubject = new BehaviorSubject<AuthLoginResult | undefined>(undefined);
        this.currentUser$ = this.currentUserSubject.asObservable();
        this.isLoading$ = this.isLoadingSubject.asObservable();
        const subscr = this.getUserByToken().subscribe();
    }

    get currentUserValue(): AuthLoginResult | undefined {
        return this.currentUserSubject.value;
    }

    set currentUserValue(user: AuthLoginResult | undefined) {
        this.currentUserSubject.next(user);
    }
    public login(loginDetail: AuthLoginModel): Observable<AuthLoginResult>
    {
        // login
        this.isLoadingSubject.next(true);

        const url = `${super.getBaseUrl()}/login`;

        if (!environment.production)
            console.log("Login:Calling " + url);

        return this.http.post<AuthLoginResult>(url, loginDetail)
            .pipe(
                map(next => {
                    let o: AuthLoginResult = Object.assign(new AuthLoginResult(), next);
                    if (!this.storeAuthUserRecordInLocalStorage(o)) return new AuthLoginResult();
                    this.currentUserSubject.next(o);
                    return o;
                }),
                catchError(this.errorHandler),
                finalize(() => {
                        this.isLoadingSubject.next(false);
                    })
            );
    }

    public logout(): Observable<boolean>
    {
        // logout
        this.isLoadingSubject.next(true);

        const userrecord = this.getAuthUserRecordFromLocalStorage();
        const url = `${super.getBaseUrl()}/logout`;


        return this.http.post<boolean>(url, userrecord)
            .pipe(
                map(next => {
                    let o: AuthLoginResult = Object.assign(new AuthLoginResult(), next);
                    return true;
                }),
                catchError(err => {
                    console.log("logout error");
                    return of(err);
                }),
                finalize(() => {
                    localStorage.removeItem(this.authLocalStorageToken);
                    this.redirectToLogin();

                    this.isLoadingSubject.next(false);
                })
            );
    }

    public redirectToLogin(): void
    {
        this.router.navigate(['/login']);
    }

    public getUserByToken(): Observable<AuthLoginResult | undefined>
    {
        const auth = this.getAuthUserRecordFromLocalStorage();

        if (auth == undefined) return of(undefined);

        if (!auth || !auth.success) {
            return of(undefined);
        }
        this.currentUserSubject.next(auth);
        return of(auth);
    }

    public getUserByTokenSync(): AuthLoginResult | undefined {
        const auth = this.getAuthUserRecordFromLocalStorage();

        console.log("getUserByTokenSync", auth);

        if (!auth || !auth.success) {
          return undefined;
        }
        this.currentUserSubject.next(auth);
        return auth;
    }

    public refreshToken(loginDetail: AuthLoginResult): Observable<AuthLoginResult>
    {
        // refreshToken
        this.isLoadingSubject.next(true);

        const url = `${super.getBaseUrl()}/refreshtoken`;

        if (!environment.production)
            console.log("Login:Calling " + url);

        return this.http.post<AuthLoginResult>(url, loginDetail)
            .pipe(
                map(next => {
                    let o: AuthLoginResult = Object.assign(new AuthLoginResult(), next);
                    if (!this.storeAuthUserRecordInLocalStorage(o)) return new AuthLoginResult();
                    this.currentUserSubject.next(o);
                    return o;
                }),
                catchError(this.errorHandler),
                finalize(() => this.isLoadingSubject.next(false))
            );
    }

    private storeAuthUserRecordInLocalStorage(auth: AuthLoginResult): boolean
    {
        
        if ((auth != null) && (auth.success)) {
            localStorage.setItem(this.authLocalStorageToken, JSON.stringify(auth));
            return true;
        }

        return false;
    }

    private getAuthUserRecordFromLocalStorage(): AuthLoginResult | undefined
    {
        try {
            const lsValue = localStorage.getItem(this.authLocalStorageToken);
            if (!lsValue) return undefined;
            const authData = JSON.parse(lsValue);
            return authData;
        } catch (error) {
            console.error(error);
            return undefined;
        }

    }

    ngOnDestroy() {
    }
}
